import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FilterList from '@mui/icons-material/FilterList';
import { buttonClasses, paperClasses } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { debounce, snakeCase } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import FilterMenuItem from '~components/Filters/FilterMenuItem';
import Menu from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';

import {
  MaterialRatesFilterQuery,
  useMaterialsQuery,
  useProjectsQuery,
  useSitesQuery,
} from './materialRatesFiltersQueries';

enum MaterialRatesFilter {
  MATERIALS = 'materials',
  PROJECTS = 'projects',
  SITES = 'sites',
}

type MaterialRatesAppliedFilters = Partial<Record<MaterialRatesFilter, string[]>>;

export interface MaterialRatesFiltersProps {
  appliedFilters: MaterialRatesAppliedFilters;
  companyId: string;
  customerAccountId?: string;
  onAppliedFiltersChange: (appliedFilters: MaterialRatesAppliedFilters) => void;
}

interface SearchQueryState {
  debounced: string;
  input: string;
}

export default function MaterialRatesFilters({
  appliedFilters,
  companyId,
  customerAccountId,
  onAppliedFiltersChange,
}: MaterialRatesFiltersProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<MaterialRatesFilter>();
  const [searchQuery, setSearchQuery] = useState<SearchQueryState>({
    input: '',
    debounced: '',
  });

  const materialsQuery = useMaterialsQuery(companyId);
  const sitesQuery = useSitesQuery(companyId);
  const projectsQuery = useProjectsQuery(companyId, customerAccountId ?? '');

  const allQueries = useMemo(() => {
    const queries: Record<MaterialRatesFilter, MaterialRatesFilterQuery> = {
      [MaterialRatesFilter.MATERIALS]: materialsQuery,
      [MaterialRatesFilter.PROJECTS]: projectsQuery,
      [MaterialRatesFilter.SITES]: sitesQuery,
    };

    return queries;
  }, [materialsQuery, sitesQuery, projectsQuery]);

  const activeQuery = useMemo(() => {
    if (!selectedFilter) {
      return null;
    }

    return allQueries[selectedFilter];
  }, [allQueries, selectedFilter]);

  const applicableFilters = useMemo(() => {
    if (customerAccountId) {
      return Object.values(MaterialRatesFilter);
    }

    return Object.values(MaterialRatesFilter).filter((filter) => {
      return filter !== MaterialRatesFilter.PROJECTS;
    });
  }, [customerAccountId]);

  const handleSearchValueChange = useCallback((value: string) => {
    setSearchQuery((state) => ({ ...state, input: value }));
  }, []);

  const handleFetchMore = useMemo(() => {
    return activeQuery?.fetchMore;
  }, [activeQuery?.fetchMore]);

  const handleSelectedOptionsChange = useCallback(
    (selectedOptions: string[]) => {
      if (selectedFilter) {
        const options = selectedOptions.length > 0 ? selectedOptions : undefined;

        onAppliedFiltersChange({ [selectedFilter]: options });
      }
    },
    [selectedFilter, onAppliedFiltersChange],
  );

  const debouncedSetSearchQuery = useRef(
    debounce((value: string) => {
      setSearchQuery((state) => ({ ...state, debounced: value }));
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
  ).current;

  useEffect(() => {
    activeQuery?.fetch(searchQuery.debounced);
  }, [activeQuery?.fetch, searchQuery.debounced]);

  useEffect(() => {
    debouncedSetSearchQuery(searchQuery.input);

    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery, searchQuery.input]);

  useEffect(() => {
    if (!isMenuOpen) {
      setSelectedFilter(undefined);
      setSearchQuery((state) => ({ ...state, input: '' }));
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (!selectedFilter) {
      materialsQuery.clear();
      projectsQuery.clear();
      sitesQuery.clear();
    }
  }, [selectedFilter, materialsQuery.clear, sitesQuery.clear, projectsQuery.clear]);

  useEffect(() => {
    projectsQuery.clear();
  }, [customerAccountId, projectsQuery.clear]);

  return (
    <Menu
      sx={{ [`& .${paperClasses.root}`]: { width: '250px' } }}
      onOpenStateChanged={setIsMenuOpen}
      menuTrigger={
        <SmallButton
          size="small"
          startIcon={<FilterList />}
          sx={(theme) => ({
            [`&.${buttonClasses.root}`]: {
              backgroundColor: 'background.paper',
              color: theme.palette.text.primary,
              border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              borderRadius: theme.brandV2.borderRadius,
            },
          })}
        >
          {t('common.filters')}
        </SmallButton>
      }
    >
      {selectedFilter ? (
        <FilterMenuItem
          focusSearchFieldOnMount
          loadingReason={activeQuery?.loadingReason}
          onFetchMore={handleFetchMore}
          onSearchValueChange={handleSearchValueChange}
          onSelectedOptionsChange={handleSelectedOptionsChange}
          options={activeQuery?.data ?? []}
          searchValue={searchQuery.input}
          selectAllOptionLabel={`${t('dispatch.dispatch_v2.filters.all_entities', { entity: t(`dispatch.dispatch_v2.filters.${snakeCase(selectedFilter)}`) })}`}
          selectedOptions={appliedFilters[selectedFilter] ?? []}
        />
      ) : (
        applicableFilters.map((filter) => (
          <MenuItem key={filter} onClick={() => setSelectedFilter(filter)}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Typography
                sx={(theme) => ({ color: theme.brandV2.colors.treadBlack })}
                variant="subtitle2"
              >
                {t(`administration.material_rates.filters.${snakeCase(filter)}`)}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {Boolean(appliedFilters[filter]?.length) && (
                  <Check
                    sx={(theme) => ({
                      color: theme.brandV2.colors.treadOrange,
                      fontSize: '16px',
                    })}
                  />
                )}

                <ChevronRight sx={{ fontSize: '16px' }} />
              </Box>
            </Box>
          </MenuItem>
        ))
      )}
    </Menu>
  );
}
